import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy } from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

// Pill is not a shared/exported component in this codebase — every consumer
// inlines the same tiny badge. Iter 94 fix: match the pattern used by
// AdminContractDetailModal (accepts `data-testid`) so styling stays
// consistent with the rest of the admin surfaces.
function Pill({ tone = "default", children, "data-testid": testId }) {
  const map = {
    ok: "border-emerald-500/50 text-emerald-400",
    warn: "border-[#F5B841]/50 text-[#F5B841]",
    danger: "border-[#F26D6D]/60 text-[#F26D6D]",
    accent: "border-[#2DD4A7]/50 text-[#2DD4A7]",
    default: "border-white/15 text-[#8B97AE]",
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${map[tone] || map.default}`}>
      {children}
    </span>
  );
}

/**
 * Iter 94 — Shared Topup Detail modal.
 *
 * Opened from BOTH:
 *   • TOPUPS tab rows (admin dashboard)
 *   • "Credit purchases" block inside the Active Streamer modal
 *
 * Both surfaces call the SAME PATCH /api/credit-topups/{tid} endpoints for
 * confirm and reject (same Iter 93 guards: editable grantedAmount prefilled
 * with requested, over-grant 409 typo guard, already-confirmed 409, reject
 * requires reason and fans out email + ticket). This component is the ONLY
 * place the two dialog UIs are implemented — the old inline duplicates in
 * AdminDashboard are removed.
 *
 * Terminology: 1 Credit = 1 USD. Credits / Platform Account entitlement.
 */
export default function TopupDetailModal({ topup, streamer, onClose, onChanged }) {
  const [fresh, setFresh] = useState(topup || null);
  const [busy, setBusy] = useState(false);
  // Confirm-form state (prefilled with requested amount)
  const [granted, setGranted] = useState(String(topup?.amount ?? ""));
  const [confirmNote, setConfirmNote] = useState("");
  // Reject-form state
  const [rejectReason, setRejectReason] = useState("");

  const reload = useCallback(async () => {
    if (!topup?.id) return;
    // No dedicated detail endpoint; refetch the streamer-scoped list & pick.
    try {
      const { data } = await api.get(`/credit-topups`);
      const found = (data || []).find((t) => t.id === topup.id);
      if (found) setFresh(found);
    } catch (e) { /* silent — modal already has an initial doc */ }
  }, [topup?.id]);
  useEffect(() => { setFresh(topup); setGranted(String(topup?.amount ?? "")); setConfirmNote(""); setRejectReason(""); }, [topup]);

  if (!topup) return null;
  const t = fresh || topup;
  const status = t.status || "—";
  const actionable = status === "awaiting_transfer" || status === "submitted";
  const tx = t.txHash || t.txReference || "";

  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); } };

  const submitConfirm = async (opts = {}) => {
    const g = Number(granted);
    if (!Number.isFinite(g) || g <= 0) { toast.error("Credits to grant must be greater than zero."); return; }
    setBusy(true);
    try {
      await api.patch(`/credit-topups/${t.id}`, {
        status: "confirmed",
        grantedAmount: g,
        reason: (confirmNote || "").trim() || undefined,
        confirmOverGrant: !!opts.overGrant,
      });
      toast.success(`Top-up confirmed — granted ${g.toLocaleString()} credits`);
      await reload();
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "over_grant_needs_confirm") {
        if (window.confirm(`Granting ${d.granted} > requested ${d.requested}. Confirm the over-grant?`)) {
          await submitConfirm({ overGrant: true });
        }
      } else {
        toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
      }
    } finally { setBusy(false); }
  };

  const submitReject = async () => {
    if ((rejectReason || "").trim().length < 3) { toast.error("A rejection reason is required (min 3 characters)."); return; }
    setBusy(true);
    try {
      await api.patch(`/credit-topups/${t.id}`, { status: "rejected", reason: rejectReason.trim() });
      toast.success("Top-up rejected — streamer notified via email + ticket");
      await reload();
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  const badgeTone = { confirmed: "ok", rejected: "danger", submitted: "warn", awaiting_transfer: "warn" }[status] || "default";

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="topup-detail-modal" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[#0B1220] border border-white/20 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Admin · Top-up detail</div>
            <div className="font-display font-bold text-lg text-[#E8ECF4]" data-testid="topup-detail-heading">#{t.seq ?? "—"} · {streamer?.displayName || streamer?.alias || t.streamerId?.slice(0, 8) || "streamer"}</div>
          </div>
          <button data-testid="topup-detail-close" onClick={onClose} className="text-[#8B97AE] hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs font-mono">
            <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Status</span><div><Pill tone={badgeTone} data-testid="topup-detail-status">{status}</Pill></div></div>
            <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Seq</span><div className="text-[#E8ECF4]" data-testid="topup-detail-seq">#{t.seq ?? "—"}</div></div>
            <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Requested</span><div className="text-[#E8ECF4] tabular-nums" data-testid="topup-detail-requested">{Number(t.amount || 0).toLocaleString()} {t.currency}</div></div>
            <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Network</span><div className="text-[#E8ECF4]">{t.network || "—"}</div></div>
            <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Created</span><div className="text-[#8B97AE]">{(t.createdAt || "").slice(0, 19).replace("T", " ")}</div></div>
            <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Confirmed / Rejected at</span><div className="text-[#8B97AE]" data-testid="topup-detail-confirmed-at">{(t.confirmedAt || t.rejectedAt || "").slice(0, 19).replace("T", " ") || "—"}</div></div>
            {typeof t.grantedAmount === "number" && (
              <div className="col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Granted</span><div className={t.grantedAmount === t.amount ? "text-emerald-400" : "text-[#F5B841]"} data-testid="topup-detail-granted">{Number(t.grantedAmount).toLocaleString()} credits{t.grantedAmount !== t.amount && ` (shortfall ${Number(t.amount - t.grantedAmount).toLocaleString()})`}</div></div>
            )}
          </div>

          <div className="border border-white/10 p-3" data-testid="topup-detail-tx">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">Transaction hash</div>
            <div className="flex items-start gap-2">
              <div className="font-mono text-[11px] text-[#E8ECF4] break-all leading-snug flex-1">{tx || "—"}</div>
              {tx && <button data-testid="topup-detail-tx-copy" onClick={() => copy(tx)} className="shrink-0 inline-flex items-center gap-1 border border-white/20 px-2 py-1 text-[10px] font-mono uppercase tracking-widest hover:border-[#F5B841] hover:text-[#F5B841]"><Copy className="w-3 h-3" />Copy</button>}
            </div>
          </div>

          {t.adminNote && (
            <div className="border border-white/10 p-3 text-xs font-mono text-[#8B97AE]" data-testid="topup-detail-admin-note">
              <div className="text-[10px] uppercase tracking-widest mb-1">Admin note</div>
              <div className="text-[#E8ECF4]">{t.adminNote}</div>
            </div>
          )}

          {actionable ? (
            <>
              <div className="border border-emerald-500/40 bg-emerald-500/[0.04] p-3 space-y-2" data-testid="topup-detail-confirm-panel">
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Confirm & credit</div>
                <div className="text-[10px] font-mono text-[#8B97AE] leading-relaxed">1 Credit = 1 USD. Small shortfalls from rounding/fees may be granted in full as goodwill; large shortfalls should be granted only for what arrived.</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Credits to grant</Label>
                    <Input data-testid="topup-detail-granted-input" type="number" min="0" step="0.01" value={granted} onChange={(e) => setGranted(e.target.value)} className="mt-1 rounded-none bg-transparent border-white/20 h-9 font-mono tabular-nums text-right" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Note (recommended if adjusting)</Label>
                    <Input data-testid="topup-detail-note-input" value={confirmNote} onChange={(e) => setConfirmNote(e.target.value)} placeholder='e.g. "$997 received, goodwill to 1000"' className="mt-1 rounded-none bg-transparent border-white/20 h-9 font-mono text-xs" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button data-testid="topup-detail-confirm-submit" onClick={() => submitConfirm()} disabled={busy} className="rounded-none bg-emerald-500 text-black hover:bg-emerald-400 h-9 font-mono text-[11px] uppercase tracking-widest disabled:opacity-40">Confirm & credit</Button>
                </div>
              </div>
              <div className="border border-[#F26D6D]/40 bg-[#F26D6D]/[0.04] p-3 space-y-2" data-testid="topup-detail-reject-panel">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]">Reject & notify</div>
                <textarea data-testid="topup-detail-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="Explain why — this appears in the email + ticket the streamer receives." className="w-full rounded-none bg-transparent border border-white/20 p-2 font-mono text-xs" />
                <div className="text-[10px] font-mono text-[#8B97AE]">Streamer will receive an email + a support ticket with this reason.</div>
                <div className="flex justify-end">
                  <Button data-testid="topup-detail-reject-submit" onClick={submitReject} disabled={busy} className="rounded-none bg-[#F26D6D] text-black hover:bg-[#E8ECF4] h-9 font-mono text-[11px] uppercase tracking-widest disabled:opacity-40">Reject & notify</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="border border-white/10 p-3 text-xs font-mono text-[#8B97AE]" data-testid="topup-detail-readonly-note">
              This top-up is in a final state ({status}). It cannot be re-confirmed or re-rejected — the guard on the backend refuses double-credits and requires a new top-up for further movement.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
